
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ReviewFiltersComponent } from "@/components/reviews/review-filters"
import { ReviewsListTable } from "@/components/reviews/reviews-list-table"
import { ReviewDetailDialog } from "@/components/reviews/review-detail-dialog"
import { ReviewStatsDashboard } from "@/components/reviews/review-stats-dashboard"
import { useReviews } from "@/api/reviews"
import type { ReviewFilters } from "@/api/reviews"
import { PageHeader } from "@/components/shared/page-header"

export default function ReviewsPage() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ReviewFilters>({
    page: 1,
    limit: 20,
    gymId: '',
    reviewType: '',
    minRating: undefined,
    startDate: '',
    endDate: '',
  })
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null)

  const { data: reviewsData, isLoading } = useReviews(filters)

  const handleFiltersChange = (newFilters: ReviewFilters) => {
    setFilters(newFilters)
  }

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page })
  }

  const handleViewDetails = (reviewId: string) => {
    setSelectedReviewId(reviewId)
  }

  const handleCloseDialog = () => {
    setSelectedReviewId(null)
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t("reviews.title")} />

      <Tabs defaultValue="all" className="space-y-6">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="all">{t("reviews.allReviews")}</TabsTrigger>
          <TabsTrigger value="statistics">{t("reviews.statistics")}</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("reviews.filterByType")}</CardTitle>
            </CardHeader>
            <CardContent>
              <ReviewFiltersComponent
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("reviews.reviewsList")}</CardTitle>
            </CardHeader>
            <CardContent>
              <ReviewsListTable
                reviews={reviewsData?.reviews || []}
                isLoading={isLoading}
                onViewDetails={handleViewDetails}
                currentPage={reviewsData?.page || 1}
                totalPages={reviewsData?.totalPages || 1}
                total={reviewsData?.total || 0}
                onPageChange={handlePageChange}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics">
          <ReviewStatsDashboard gymId={filters.gymId} />
        </TabsContent>
      </Tabs>

      <ReviewDetailDialog
        reviewId={selectedReviewId}
        open={!!selectedReviewId}
        onClose={handleCloseDialog}
      />
    </div>
  )
}
